import shopifyIcon from 'assets/images/integrations/shopify.svg';

import type { ConversionRule } from 'modules/pixel/pixelApiTypes';

import { CodeBlock } from '../CodeBlock';
import type { IntegrationContext, IntegrationSnippet, PixelIntegration } from './types';

/**
 * Shopify integration — two-step install:
 *
 * 1. Universal pixel in {@code layout/theme.liquid} — for page-view auto-tracking and any
 *    trigger-based rules pixel.js can resolve from the storefront DOM.
 * 2. A Custom Pixel under <strong>Settings → Customer events</strong> — Shopify's sandboxed
 *    JS environment where {@code analytics.subscribe('event_name', cb)} hooks into native
 *    Shopify events (product_viewed, checkout_completed, …). HTML isn't allowed in a Custom
 *    Pixel (sandbox), so the universal {@code <script>} goes in the theme, not here.
 */
export const shopifyIntegration: PixelIntegration = {
  id: 'shopify',
  label: 'Shopify',
  description:
    'Install the universal pixel in your theme, then add a Custom Pixel that subscribes to Shopify\'s Customer Events and forwards them to our S2S endpoint.',
  icon: <img src={shopifyIcon} alt="" className="h-4 w-4" />,
  docsHref: 'https://help.shopify.com/en/manual/promoting-marketing/pixels/custom-pixels/code',
  renderSetup: (ctx) => (
    <div className="flex flex-col gap-4">
      <Step number={1} title="Install the universal pixel in your theme">
        <p className="mb-2 text-base-xs text-primary-gray-500">
          In Shopify admin go to <strong>Sales channels → Online Store → Themes</strong>, pick the
          live theme, click <strong>Edit code</strong>, open{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">
            layout/theme.liquid
          </code>
          , and paste this snippet right after the opening{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">
            &lt;head&gt;
          </code>{' '}
          tag:
        </p>
        <CodeBlock language="html" code={ctx.pixelScriptTag} />
      </Step>

      <Step number={2} title="Add a Custom Pixel for Customer Events">
        <p className="text-base-xs text-primary-gray-500">
          Custom Pixels run in Shopify&apos;s sandboxed JS environment and are where{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">
            analytics.subscribe(...)
          </code>{' '}
          lives. Set one up once per store:
        </p>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-base-xs text-primary-gray-600">
          <li>
            In Shopify admin go to <strong>Settings → Customer events</strong>.
          </li>
          <li>
            Click <strong>Add custom pixel</strong> and name it <em>CoolAI conversions</em> (or
            anything memorable).
          </li>
          <li>
            Under <strong>Permissions</strong> set both <em>Customer privacy</em> and{' '}
            <em>Data sale</em> to whatever matches the store&apos;s policy (typically{' '}
            <em>Not required</em> for first-party).
          </li>
          <li>
            Paste each rule&apos;s snippet from Step 3 below into the <strong>Code</strong> editor —
            they can be stacked back-to-back.
          </li>
          <li>
            Click <strong>Save</strong>, then <strong>Connect</strong> to activate.
          </li>
        </ol>
        <p className="mt-2 text-[11px] text-primary-gray-500">
          Heads up: Custom Pixels accept JavaScript only — no{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">
            &lt;script&gt;
          </code>{' '}
          tags. That&apos;s why the universal pixel goes in the theme (Step 1), and{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">
            analytics.subscribe
          </code>{' '}
          lives in the Custom Pixel (Step 2).
        </p>
      </Step>
    </div>
  ),
  buildSnippetForRule: (rule, ctx) => buildShopifySnippet(rule, ctx),
};

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-electric-100 text-[11px] font-semibold text-primary-electric-700">
          {number}
        </span>
        <h4 className="text-base-sm font-medium text-primary-gray-900">{title}</h4>
      </div>
      <div className="pl-7">{children}</div>
    </div>
  );
}

/**
 * Map our rule.category → a sensible default Shopify Customer Event + how to pull
 * value/currency/order_number off the Shopify event payload. TAMs can edit the event name
 * in the snippet if a different Shopify event fits the rule better.
 *
 * Shopify standard events reference:
 * https://shopify.dev/docs/api/web-pixels-api/standard-events
 */
const CATEGORY_TO_SHOPIFY: Record<string, { event: string; extras?: string[] }> = {
  pagevisit: { event: 'page_viewed' },
  leads: { event: 'form_submitted' },
  checkout: {
    event: 'checkout_completed',
    extras: [
      'amount: event.data.checkout.totalPrice.amount',
      'currency: event.data.checkout.totalPrice.currencyCode',
      'order_number: String(event.data.checkout.order.id).split(\'/\').pop()',
    ],
  },
  activity: { event: 'product_viewed' },
};

function buildShopifySnippet(rule: ConversionRule, ctx: IntegrationContext): IntegrationSnippet {
  const mapping = (rule.category && CATEGORY_TO_SHOPIFY[rule.category]) || {
    event: '<shopify_event>',
  };
  const dataObjectLines: string[] = [`category: ${JSON.stringify(rule.category)}`];

  if (rule.subcategory) dataObjectLines.push(`subcategory: ${JSON.stringify(rule.subcategory)}`);
  const extractorKeys = rule.extractors ? Object.keys(rule.extractors) : [];

  if (extractorKeys.length > 0 && mapping.extras) {
    for (const extra of mapping.extras) {
      if (extractorKeys.some((k) => extra.startsWith(k + ':'))) {
        dataObjectLines.push(extra);
      }
    }
  } else if (extractorKeys.length > 0) {
    for (const key of extractorKeys) {
      const placeholder =
        key === 'amount'
          ? '0           // your order total'
          : key === 'currency'
            ? '\'USD\'      // your order currency'
            : key === 'order_number'
              ? '\'\'        // your unique order id'
              : '\'\'';

      dataObjectLines.push(`${key}: ${placeholder}`);
    }
  }
  const dataObject = dataObjectLines.map((l) => `        ${l},`).join('\n');

  const code = [
    '// Subscribe to a Shopify Customer Event and forward to your CoolAI pixel.',
    `// If '${mapping.event}' isn't the right event for "${rule.name}", swap it for the`,
    '// matching one (product_added_to_cart, checkout_started, search_submitted, ...).',
    '// Full list: https://shopify.dev/docs/api/web-pixels-api/standard-events',
    `analytics.subscribe('${mapping.event}', function(event) {`,
    `  fetch('${ctx.s2sEndpoint}', {`,
    '    method: \'POST\',',
    '    headers: { \'Content-Type\': \'application/json\' },',
    '    body: JSON.stringify({',
    `      pixel_code: '${ctx.pixelCode}',`,
    '      document_data: { href: event.context.document.location.href },',
    '      referrer: event.context.document.referrer || \'\',',
    '      data_object: {',
    dataObject,
    '      },',
    '    }),',
    '    keepalive: true,',
    '  });',
    '});',
  ].join('\n');

  return {
    code,
    language: 'js',
    label: 'Paste into the Custom Pixel code editor',
  };
}
